import React, { useContext } from "react";
import { motion } from "framer-motion";
import { BsArrowRight } from "react-icons/bs";
import { CursorContext } from "../context/CursorContext";
import { ScrollLink } from "../components/ScrollLink";
import { transition1 } from "../transitions";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { services } from "../Data.js";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.65, ease: "easeOut" },
  }),
};

export default function Service() {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);

  return (
    <motion.section
      id="service"
      className="section snap-start min-h-screen flex flex-col lg:pt-36 pt-24 pb-20 overflow-y-auto"
      initial={{ opacity: 0, y: "5%" }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      exit={{ opacity: 0, y: "5%" }}
      transition={transition1}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      <div className="container mx-auto px-6 flex flex-col h-full max-w-5xl">
        {/* ── Header ── */}
        <motion.div
          className="mb-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <motion.h1 className="h1 font-secondary" variants={fadeUp} custom={0}>
            Services
          </motion.h1>
          <motion.p
            className="mt-2 text-sm leading-relaxed max-w-xl"
            variants={fadeUp}
            custom={1}
          >
            Scalable, secure, and maintainable — here's what I build and how I
            can help your product grow.
          </motion.p>
        </motion.div>

        {/* ── Carousel ── */}
        <motion.div
          className="flex-1 min-h-0 flex flex-col"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="flex-1 min-h-0">
            <Swiper
              modules={[Pagination]}
              pagination={{ clickable: true, el: ".service-pagination" }}
              spaceBetween={24}
              slidesPerView={1}
              breakpoints={{
                640: { slidesPerView: 1.4 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 2.5 },
              }}
              className="w-full h-full cstm-swiper-service"
            >
              {services.map((service, i) => (
                <SwiperSlide key={i} className="h-auto">
                  <motion.div
                    className="relative rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-white/5 backdrop-blur-sm p-6 flex flex-col gap-5 group overflow-hidden h-full"
                    whileHover={{ y: -5, transition: { duration: 0.22 } }}
                  >
                    {/* icon */}
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: service.color + "18",
                        border: `1px solid ${service.color}33`,
                      }}
                    >
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-8 h-8 object-contain"
                      />
                    </div>

                    {/* title */}
                    <h3 className="text-lg font-semibold font-primary">
                      {service.title}
                    </h3>

                    {/* description */}
                    <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400 flex-1">
                      {service.description}
                    </p>

                    {/* tech pills */}
                    <div className="flex flex-wrap gap-2">
                      {service.tags.map((tag, j) => (
                        <span
                          key={j}
                          className="rounded-full px-3 py-1 text-xs font-medium"
                          style={{
                            backgroundColor: service.color + "18",
                            color: service.color,
                            border: `1px solid ${service.color}33`,
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* hover bottom accent */}
                    <div
                      className="absolute inset-x-0 bottom-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ backgroundColor: service.color }}
                    />
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* ── Pagination dots — own row, so it never overlaps the cards ── */}
          <div className="service-pagination flex items-center justify-center gap-2 mt-4 sm:mt-5 shrink-0" />
        </motion.div>

        {/* ── CTA ── */}
        <motion.div
          className="shrink-0 mt-2 mb-1"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <ScrollLink
            section="contact"
            className="btn max-w-fit hover:scale-105 transition-transform duration-300"
          >
            Hire Me
            <span className="ml-1">
              <BsArrowRight />
            </span>
          </ScrollLink>
        </motion.div>
      </div>
    </motion.section>
  );
}
